import type { OptionInputObject } from '../../select/shared/normalize-options';
import type { ReactNode } from 'react';
import type { DropdownCloseAction } from '../../select/typeahead/types';

export type DawaSuggestionAddressData = {
    href?: string;
    id?: string;
    vejnavn?: string;
    adresseringsvejnavn?: string;
    husnr?: string;
    etage?: string | null;
    dør?: string | null;
    supplerendebynavn?: string | null;
    postnr?: string;
    postnrnavn?: string;
    navn?: string;
    [key: string]: unknown;
};

export type DawaAddressInfo = {
    streetName: string;
    houseNumber: string;
    floor: string;
    door: string;
    zipCode: string;
    postalCodeName: string;
    supCityName: string;
};

export type DawaFormattedAddress = {
    address: string;
    addressInfo: DawaAddressInfo;
};

export const formatDawaAddress = (data: DawaSuggestionAddressData = {}): DawaFormattedAddress => {
    const streetName = (data.vejnavn ?? data.navn ?? '').trim();
    const houseNumber = (data.husnr ?? '').trim();
    const floor = (data.etage ?? '').trim();
    const door = (data.dør ?? '').trim();
    const zipCode = (data.postnr ?? '').trim();
    const postalCodeName = (data.postnrnavn ?? '').trim();
    const supCityName = (data.supplerendebynavn ?? '').trim();

    const primaryLine = [streetName, houseNumber].filter(Boolean).join(' ').trim();
    const floorDoorParts = [floor, door].filter(Boolean);
    const floorDoorLine = floorDoorParts.length > 0 ? floorDoorParts.join('. ') : '';
    const cityLine = [zipCode, postalCodeName].filter(Boolean).join(' ').trim();

    const segments = [primaryLine, floorDoorLine, cityLine].filter(Boolean);
    if (supCityName) {
        segments.push(supCityName);
    }

    return {
        address: segments.join(', '),
        addressInfo: {
            streetName,
            houseNumber,
            floor,
            door,
            zipCode,
            postalCodeName,
            supCityName,
        },
    };
};

type DawaSuggestionForOption = {
    id: string;
    value: string;
    label: string;
};

type SuggestionOptionMeta<TSuggestion> = {
    kind: 'suggestion';
    suggestion: TSuggestion;
    closeBehavior: DropdownCloseAction;
};

type CustomOptionMeta = {
    kind: 'custom';
    rawValue: string;
    displayLabel: ReactNode;
    closeBehavior: DropdownCloseAction;
};

export type AddressFieldOptionMeta<TSuggestion extends DawaSuggestionForOption> = SuggestionOptionMeta<TSuggestion> | CustomOptionMeta;

export type DawaOption<TSuggestion extends DawaSuggestionForOption> = OptionInputObject<AddressFieldOptionMeta<TSuggestion>>;

type MapDawaSuggestionToOptionArgs = {
    highlightQuery?: string;
};

export const mapDawaSuggestionToOption = <TSuggestion extends DawaSuggestionForOption>(
    suggestion: TSuggestion,
    _options: MapDawaSuggestionToOptionArgs = {}
): DawaOption<TSuggestion> => {
    return {
        value: suggestion.value,
        label: suggestion.label,
        meta: {
            kind: 'suggestion',
            suggestion,
            closeBehavior: 'keep-open',
        },
    };
};
